/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { AUTH_ENDPOINTS } from '../endpoints';

/**
 * Safely read from localStorage. Returns null instead of throwing when storage access is blocked
 * (Safari private mode, disabled cookies, sandboxed/third-party context) — which otherwise surfaces as
 * an uncaught SecurityError during auth init (AQRATE-PORTAL-1C).
 * @param {string} key
 * @returns {string|null}
 */
const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * @typedef {Object} LoginDto
 * @property {string} email
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} profilePicture
 * @property {string} googleId
 */

/**
 * @typedef {Object} EmailLoginDto
 * @property {string} email - Email address (max 320 chars)
 * @property {string} password - Password (6-100 chars)
 * @property {string} [browserTimezone] - IANA timezone from browser
 */

/**
 * @typedef {Object} RegisterDto
 * @property {string} email - Email address (max 320 chars)
 * @property {string} password - Password (6-100 chars)
 * @property {string} firstName - First name (max 200 chars)
 * @property {string} lastName - Last name (max 200 chars)
 * @property {string} [profilePicture] - Profile picture URL (max 500 chars)
 */

/**
 * @typedef {Object} AuthResponse
 * @property {string} token - JWT access token
 * @property {string} refreshToken - JWT refresh token
 * @property {Object} user - User data
 */

const authService = {
  /**
   * Login with Google OAuth
   * @param {LoginDto} loginData - Google OAuth login data
   * @returns {Promise<AuthResponse>}
   */
  googleLogin: async (loginData) => {
    return apiRequest(() =>
      axiosInstance.post(AUTH_ENDPOINTS.GOOGLE_LOGIN, loginData)
    );
  },

  /**
   * Login with email and password
   * @param {EmailLoginDto} credentials - Email and password
   * @returns {Promise<AuthResponse>}
   */
  emailLogin: async (credentials) => {
    return apiRequest(() =>
      axiosInstance.post(AUTH_ENDPOINTS.EMAIL_LOGIN, credentials)
    );
  },

  /**
   * Register a new user
   * @param {RegisterDto} userData - User registration data
   * @returns {Promise<AuthResponse>}
   */
  register: async (userData) => {
    return apiRequest(() =>
      axiosInstance.post(AUTH_ENDPOINTS.REGISTER, userData)
    );
  },

  /**
   * Get current authenticated user
   * @returns {Promise<Object>} Current user data
   */
  getCurrentUser: async () => {
    return apiRequest(() =>
      axiosInstance.get(AUTH_ENDPOINTS.ME)
    );
  },

  /**
   * Refresh authentication token
   * @returns {Promise<AuthResponse>} New tokens
   */
  refreshToken: async () => {
    return apiRequest(() =>
      axiosInstance.post(AUTH_ENDPOINTS.REFRESH_TOKEN)
    );
  },

  /**
   * Revoke refresh token on server
   * @param {string} refreshToken - Refresh token to revoke
   * @returns {Promise<void>}
   */
  revokeToken: async (refreshToken) => {
    try {
      await apiRequest(() =>
        axiosInstance.post(AUTH_ENDPOINTS.REVOKE_TOKEN, { refreshToken })
      );
    } catch (error) {
      // Ignore errors during token revocation (e.g., token already expired)
      console.warn('Token revocation failed:', error);
    }
  },

  /**
   * Create a QR login session for mobile device authentication
   * @returns {Promise<{sessionId: string, expiresAt: string}>}
   */
  createQrLoginSession: async () => {
    return apiRequest(() =>
      axiosInstance.post(AUTH_ENDPOINTS.QR_LOGIN_SESSION)
    );
  },

  /**
   * Get the status of a QR login session
   * @param {string} sessionId - The session ID to check
   * @returns {Promise<{status: 'pending' | 'completed' | 'expired'}>}
   */
  getQrLoginSessionStatus: async (sessionId) => {
    return apiRequest(() =>
      axiosInstance.get(AUTH_ENDPOINTS.QR_LOGIN_SESSION_STATUS(sessionId))
    );
  },

  /**
   * Request a password reset email
   * @param {string} email - User's email address
   * @returns {Promise<Object>}
   */
  forgotPassword: async (email) => {
    return apiRequest(() =>
      axiosInstance.post(AUTH_ENDPOINTS.FORGOT_PASSWORD, { email })
    );
  },

  /**
   * Reset password using a token
   * @param {string} token - Password reset token
   * @param {string} newPassword - New password
   * @returns {Promise<Object>}
   */
  resetPassword: async (token, newPassword) => {
    return apiRequest(() =>
      axiosInstance.post(AUTH_ENDPOINTS.RESET_PASSWORD, { token, newPassword })
    );
  },

  /**
   * Verify email using a token
   * @param {string} token - Email verification token
   * @returns {Promise<Object>}
   */
  verifyEmail: async (token) => {
    return apiRequest(() =>
      axiosInstance.post(AUTH_ENDPOINTS.VERIFY_EMAIL, { token })
    );
  },

  /**
   * Resend email verification
   * @param {string} email - User's email address
   * @returns {Promise<Object>}
   */
  resendVerification: async (email) => {
    return apiRequest(() =>
      axiosInstance.post(AUTH_ENDPOINTS.RESEND_VERIFICATION, { email })
    );
  },

  /**
   * Logout user
   * Revokes refresh token on server and clears local storage
   */
  logout: async () => {
    const refreshToken = safeGetItem('refreshToken');

    // Revoke token on server if it exists
    if (refreshToken) {
      await authService.revokeToken(refreshToken);
    }

    // Clear local storage
    localStorage.removeItem('localToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('fullName');
    localStorage.removeItem('role');
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated: () => {
    const token = safeGetItem('localToken');
    const userData = safeGetItem('userData');
    return !!(token && userData);
  },

  /**
   * Get stored auth token
   * @returns {string|null}
   */
  getToken: () => {
    return safeGetItem('localToken');
  },

  /**
   * Get stored user data
   * @returns {Object|null}
   */
  getUserData: () => {
    const userData = safeGetItem('userData');
    try {
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  },

  /**
   * Store authentication data
   * @param {string} token - JWT access token
   * @param {string} refreshToken - JWT refresh token
   * @param {Object} user - User data
   */
  storeAuthData: (token, refreshToken, user) => {
    localStorage.setItem('localToken', token);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    localStorage.setItem('userData', JSON.stringify(user));
    if (user.fullName) {
      localStorage.setItem('fullName', user.fullName);
    }
    if (user.role) {
      localStorage.setItem('role', user.role);
    }
  },
};

export default authService;
